import { KIOSK_CHAT_USERNAMES } from '../config';

export type KioskChatStatus = 'pending' | 'resolved' | 'failed';

const STORAGE_KEY = 'tt-kiosk-chats';

let chatIdsByUsername: Record<string, string> | undefined;
const failedUsernames = new Set<string>();
const listeners = new Set<NoneToVoidFunction>();

export function getKioskChatIds() {
  const map = loadChatIds();

  return KIOSK_CHAT_USERNAMES.map((username) => map[username]).filter(Boolean);
}

export function isKioskUsername(username?: string) {
  if (!username) return false;
  const lowered = username.toLowerCase();

  return KIOSK_CHAT_USERNAMES.some((kioskUsername) => kioskUsername.toLowerCase() === lowered);
}

export function getKioskChatId(username: string) {
  return loadChatIds()[username];
}

export function setKioskChatId(username: string, chatId: string) {
  const map = loadChatIds();
  map[username] = chatId;
  failedUsernames.delete(username);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (err) {
    // Storage may be unavailable (private mode, blocked site data)
  }

  notifyListeners();
}

// Closing a chat (`chatId` undefined) is always allowed
export function isKioskAllowedChat(chatId?: string) {
  return !chatId || Object.values(loadChatIds()).includes(chatId);
}

export function getKioskChatStatus(username: string): KioskChatStatus {
  if (getKioskChatId(username)) return 'resolved';

  return failedUsernames.has(username) ? 'failed' : 'pending';
}

export function markKioskChatFailed(username: string) {
  failedUsernames.add(username);
  notifyListeners();
}

export function clearKioskChatFailed(username: string) {
  failedUsernames.delete(username);
  notifyListeners();
}

export function subscribeKioskChats(listener: NoneToVoidFunction) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function loadChatIds() {
  if (chatIdsByUsername) return chatIdsByUsername;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) as unknown : undefined;
    chatIdsByUsername = parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {};
  } catch (err) {
    chatIdsByUsername = {};
  }

  return chatIdsByUsername;
}

function notifyListeners() {
  listeners.forEach((listener) => listener());
}
